import { Form, Formik } from 'formik'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { Button } from 'semantic-ui-react'
import { IFormSubmission } from '../../../models/Common'
import {
  EnquiryStatuses,
  IRequestInformation,
  RequestInformationRequest,
} from '../../../models/Enquiry'
import Conditional from '../../common/Conditional'
import FWOLimitedTextAreaInput from '../../form/FWOLimitedTextAreaInput'
import * as Yup from 'yup'
import EnquiryService from '../../../services/EnquiryService'
import EnquiryContext from '../context/EnquiryContext'

export interface IEnquiryRequestForInformationFormProps {
  data: IRequestInformation
  close: () => void
  handleErrors?: (any) => void
  enquiryId: string
  enquiryNumber: string
  enquiryStatus: string
}

const EnquiryRequestForInformationForm = (
  props: IEnquiryRequestForInformationFormProps
) => {
  const mountedRef = useRef(false)
  const formRef = useRef(null)
  const context = useContext(EnquiryContext)
  const [requestInformation, setRequestInformation] =
    useState<IRequestInformation>()
  const [request, setRequest] = useState<RequestInformationRequest>()
  const [disabled, setDisabled] = useState<boolean>(false)
  const [formSubmission, setFormSubmission] = useState<IFormSubmission>({
    submitting: false,
    submitted: false,
  })
  const validateSchema = Yup.object({
    response: Yup.string().required(
      'This field cannot be empty, it is required.'
    ),
  })

  useEffect(() => {
    mountedRef.current = true
    if (mountedRef.current && props.data) {
      setRequestInformation(props.data)
      setDisabled(
        props.data.responded === 'Yes' ||
          (props.enquiryStatus != null &&
            props.enquiryStatus.toLowerCase() ===
              EnquiryStatuses.EnquiryAdviceProvided.toLowerCase())
      )
      setRequest(new RequestInformationRequest(props.data))
    }
    return () => {
      mountedRef.current = false
    }
  }, [props.data])

  const submit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const handleFormSubmit = async (values: IRequestInformation) => {
    setFormSubmission((prevState) => ({
      ...prevState,
      submitting: true,
      submitted: false,
    }))
    const payload = new RequestInformationRequest(values)
    payload.enquiryId = props.enquiryId
    await EnquiryService.updateRequestInformationRequest(payload)
      .then(() => {
        setDisabled(true)
        setFormSubmission((prevState) => ({
          ...prevState,
          submitting: false,
          submitted: true,
        }))
        context.reload()
      })
      .catch((error) => {
        console.log(error)
        window.scroll(0, 0)
      })
      .finally(() => {
        setFormSubmission((prevState) => ({
          ...prevState,
          submitting: false,
        }))
      })
  }

  return (
    <>
      <Conditional if={requestInformation && !formSubmission.submitted}>
        <Formik
          innerRef={formRef}
          enableReinitialize
          validationSchema={validateSchema}
          initialValues={request}
          validateOnMount={false}
          onSubmit={(values) => {
            handleFormSubmit(values)
          }}
        >
          {({ handleSubmit }) => (
            <Form
              className="ui form osp-variant large"
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              <div className="table-wrapper">
                <table id="table-requestinfoform">
                  <tbody>
                    <tr>
                      <td>Enquiry number</td>
                      <td>{props.enquiryNumber}</td>
                    </tr>
                    <tr>
                      <td>Date requested</td>
                      <td>{requestInformation?.createdOn}</td>
                    </tr>
                    <tr>
                      <td>Subject</td>
                      <td>{requestInformation?.subject}</td>
                    </tr>
                    <tr>
                      <td>Request for information</td>
                      <td>{requestInformation?.request}</td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        <FWOLimitedTextAreaInput
                          placeholder=""
                          rows={3}
                          required={true}
                          label="Response"
                          aria-label="Response"
                          name="response"
                          limit={2000}
                          showreadonly={disabled}
                          style={{ backgroundColor: '#fff' }}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={2}>
                        <div className="button-group is-right">
                          <Button
                            content="Back"
                            className="is-secondary"
                            onClick={props.close}
                          />
                          <Conditional if={!disabled}>
                            <Button
                              content="Submit"
                              className="is-primary"
                              type="button"
                              loading={formSubmission.submitting}
                              disabled={formSubmission.submitting}
                              onClick={submit}
                            />
                          </Conditional>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Form>
          )}
        </Formik>
      </Conditional>
      <Conditional if={requestInformation && formSubmission.submitted}>
        <h2>Thank you</h2>
        <p>
          Thank you for providing additional information. We will respond to
          your shortly.
        </p>
      </Conditional>
    </>
  )
}
export default EnquiryRequestForInformationForm
